import { useLayoutEffect, useRef } from "react";

// Module-level (not sessionStorage) so positions reset on a full page reload
// but survive unmount/remount when navigating within the SPA session
// (switching in-app tabs, going to another page and back, etc).
const scrollPositions = new Map();

// React Strict Mode (dev only) deliberately mounts every effect, then
// immediately fake-unmounts and fake-remounts it, to help catch missing
// cleanup. A real navigation-triggered unmount never happens this fast after
// mount, so skip persisting when it does - this only ever filters out the
// synthetic StrictMode cycle, never a genuine save. Without this, that
// fake-unmount can capture the container's still-transient (too-narrow)
// width and silently overwrite the real saved position with it.
const STRICT_MODE_GUARD_MS = 50;

// Content that populates via its own effect shortly after mount (e.g. header
// tooltip icons) can keep resizing the container for a bit, so keep
// re-applying the saved target while that settles, instead of a one-shot
// assignment that gets clamped to a too-small transient width and stays
// there once the container reaches its true, larger size.
const SETTLE_WINDOW_MS = 800;

// Attach the returned ref to whichever element actually scrolls (the one
// with overflow: auto/scroll) to persist its position across navigation.
// `key` must be unique per scrollable element on the site - e.g. a route
// path, optionally suffixed when a page renders more than one such element.
export const useScrollRestoration = (key) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !key) return;

    const mountedAt = performance.now();
    const saved = scrollPositions.get(key) || { x: 0, y: 0 };

    const applyPosition = () => {
      el.scrollLeft = saved.x;
      el.scrollTop = saved.y;
    };
    applyPosition();

    const handleScroll = () => {
      scrollPositions.set(key, { x: el.scrollLeft, y: el.scrollTop });
    };
    el.addEventListener("scroll", handleScroll);

    let observer;
    let timeoutId;
    if (saved.x > 0 || saved.y > 0) {
      // Observe the CONTENT (el's first child, e.g. the <table>), not el
      // itself - el has overflow: auto with a fixed CSS width/height, so its
      // own box never resizes as content grows; that's the whole point of
      // overflow: auto. Observing el here would make this retry a no-op.
      observer = new ResizeObserver(applyPosition);
      observer.observe(el.firstElementChild || el);
      timeoutId = setTimeout(() => observer.disconnect(), SETTLE_WINDOW_MS);
    }

    return () => {
      el.removeEventListener("scroll", handleScroll);
      observer?.disconnect();
      clearTimeout(timeoutId);
      if (performance.now() - mountedAt < STRICT_MODE_GUARD_MS) return;
      scrollPositions.set(key, { x: el.scrollLeft, y: el.scrollTop });
    };
  }, [key]);

  return ref;
};
